import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { useCampaignPowerDialApiV1CampaignsCampaignIdPowerDialPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import {
  activeMassJobsQueryKey,
  massCallResponseToJobSummary,
  upsertMassJob,
} from '#/shared/lib/mass-jobs'
import {
  PHONE_STATE,
  connectTelephony,
  isLiveSession,
  useTelephonyStore,
} from '#/shared/lib/telephony'
import { toast } from '#/shared/ui/sonner'

import { CAMPAIGN_CONTACT_FILTERS } from './contact-filters'
import type { CampaignContactFilter } from './contact-filters'

export interface UseCampaignPowerDialResult {
  /** Queue one slice of the audience — the Contact tab's chips, which are
   *  exactly the `filter` the endpoint takes. Everyone when omitted. */
  start: (filter?: CampaignContactFilter) => void
  isStarting: boolean
}

/**
 * Queue a campaign's people into the power dialer.
 *
 * `POST /campaigns/{id}/power-dial` is the ordinary dialer with a campaign for
 * an input — same session, same append-to-a-running-queue, same auto-rotating
 * from-number, same `power_dialer.updated` frames — so the response is a
 * `MassCallResponse` and is seeded into the mass-jobs list exactly as the list
 * pages' Auto Dial seeds it. Nothing here needs a line: the service rotates
 * them, unlike `POST /send/mass/call` where the client resolves one.
 *
 * The header's main button dials `all`, the one slice `power_dial_count`
 * describes, so its badge and its queue agree. The menu beside it picks a
 * narrower slice (`filter`); the service then leaves out whoever in it has no
 * mobile or no quote, and caps the run at 500, exactly as for `all`.
 */
export function useCampaignPowerDial(
  campaignId: string,
): UseCampaignPowerDialResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useCampaignPowerDialApiV1CampaignsCampaignIdPowerDialPost()

  const queue = async (filter: CampaignContactFilter) => {
    try {
      const response = await mutation.mutateAsync({
        campaignId,
        params: { filter },
      })
      // The response is the session just created, so the header pill's row is
      // seeded from it rather than re-pulling a list we already hold.
      const seeded = upsertMassJob(
        queryClient,
        massCallResponseToJobSummary(response.data),
      )
      if (!seeded) {
        await queryClient.invalidateQueries({
          queryKey: activeMassJobsQueryKey(),
        })
      }
    } catch (error) {
      // Both refusals are POWER_DIAL_EMPTY and each carries its own sentence —
      // "they opted out" and "nobody has both a mobile and a quote" are
      // different problems, so the service's words beat a generic one.
      toast.error(getApiErrorDetail(error) ?? t('campaigns.powerDial.error'))
    }
  }

  const start = (
    filter: CampaignContactFilter = CAMPAIGN_CONTACT_FILTERS.all,
  ) => {
    const telephony = useTelephonyStore.getState()
    // The dialer bridges onto the registered softphone; without one the agent
    // leg has nowhere to ring and the customer is left hanging.
    if (telephony.status !== PHONE_STATE.ready) {
      toast.info(t('massCall.toast.softphoneConnecting'))
      void connectTelephony()
      return
    }
    // A live regular call would get a second call bridged on top. A running
    // dialer session is fine — starting again appends to its queue.
    if (
      telephony.sessions.some(
        (session) => isLiveSession(session) && !session.powerdial,
      )
    ) {
      toast.info(t('massCall.toast.onCall'))
      telephony.requestAttention()
      return
    }
    void queue(filter)
  }

  return { start, isStarting: mutation.isPending }
}
